// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocale } from '../hooks/useLocale.js';
import { tKey } from '../utils/i18n-helpers.js';
import '../styles/toast.css';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  exiting?: boolean;
}

/**
 * The variants a screen reader must interrupt for.
 *
 * Sprint 65.15.0 — a failure the user is not told about is a failure that did
 * not happen, as far as they know. `success` and `info` stay polite; cutting
 * across a screen reader mid-sentence to say "saved" is its own defect.
 */
const ASSERTIVE_VARIANTS: ReadonlySet<ToastVariant> = new Set(['error', 'warning']);

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * The dismiss control's accessible name, in the operator's locale.
   *
   * It used to be the literal `"Close notification"`. The visible child is
   * `&times;`, which contributes nothing a screen reader can use, so that
   * literal was the ONLY name this button has ever had — every toast in the
   * product announced its dismiss control in English whatever the locale, on
   * the public setup route included.
   *
   * `useLocale()` is safe here: `App.tsx` mounts `<LocaleProvider>` OUTSIDE
   * `<ToastProvider>`, so the context is always resolved by the time this
   * runs in the product.
   *
   * `common.close_notification` rather than the `common.close` that already
   * existed: a toast carries no accessible name of its own, so a button named
   * only "Close" tells the user nothing about WHAT closes — and a page whose
   * reads fail raises several toasts at once, which a screen reader then
   * presents as a column of identical "Close" buttons.
   *
   * Read through `tKey` rather than by property access. ~150 component tests
   * render this provider under a hand-written `useLocale` stub, and a stub
   * without a `common` block would otherwise take the whole tree down with a
   * TypeError — a test-harness shape crashing the product component is the
   * defect class 61.0.10.2 spent a sprint on. The English fallback is
   * unreachable in the product: all 16 locales carry the key.
   */
  const { t } = useLocale();
  const closeLabel = tKey(
    t?.common as unknown as Record<string, unknown> | undefined,
    'close_notification',
    'Close notification',
  );

  /**
   * Every pending auto-dismiss and exit timer, so unmounting can cancel them.
   *
   * Sprint 61.0.11 gate E12.5 pass 1. `addToast` scheduled a 5-second dismiss
   * and a nested 300 ms removal and cleared neither, so a toast raised less
   * than five seconds before the tree came down fired `setToasts` into a
   * disposed tree. In the browser that is a no-op React warns about; under
   * jsdom the timer outlives the environment and throws
   * `ReferenceError: window is not defined` from React's own scheduler —
   * reported by vitest as an unhandled error, so the run exits NON-ZERO with
   * every assertion passing. It is load-dependent, which is the worst kind:
   * the same file passes alone and fails in a full suite, so it reads as a
   * flake rather than as this.
   *
   * A ref, not state: the registry must never itself cause a render.
   */
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  /** Schedule work that cancels itself if the provider goes away first. */
  const later = useCallback((fn: () => void, ms: number) => {
    const handle = setTimeout(() => {
      timers.current.delete(handle);
      fn();
    }, ms);
    timers.current.add(handle);
    return handle;
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const handle of pending) clearTimeout(handle);
      pending.clear();
    };
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = Date.now().toString() + Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, message, variant }]);
      // Auto-dismiss after 5 seconds
      later(() => {
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
        later(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 300);
      }, 5000);
    },
    [later],
  );

  const dismiss = useCallback(
    (id: string) => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
      later(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 300);
    },
    [later],
  );

  // Sprint 64.5.0 (idea-catalog #53, G-106/G-113): the context value MUST be
  // memoised. A fresh object literal here changes the context identity on every
  // toast add/dismiss, so any `useCallback` loader holding `toast` in its deps
  // array re-fires — a page whose loader fails then loops unboundedly
  // (62.2.5's gate E12.5 measured 241 loader calls in 800 ms). `addToast` is a
  // stable `useCallback`, so this value never changes for the provider's
  // lifetime — its only dependency, `later`, is itself `useCallback([])`,
  // which is why the timer registry added in 61.0.11 did not disturb this.
  // Guarded by Toast.test.tsx (identity + loop + source asserts).
  const value = useMemo<ToastContextValue>(
    () => ({
      success: (msg) => addToast(msg, 'success'),
      error: (msg) => addToast(msg, 'error'),
      warning: (msg) => addToast(msg, 'warning'),
      info: (msg) => addToast(msg, 'info'),
    }),
    [addToast],
  );

  const renderToast = (toast: Toast) => (
    <div
      key={toast.id}
      className={`dm-toast dm-toast-${toast.variant}${toast.exiting ? ' dm-toast-exit' : ''}`}
    >
      <span>{toast.message}</span>
      <button className="dm-toast-close" onClick={() => dismiss(toast.id)} aria-label={closeLabel}>
        &times;
      </button>
    </div>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        Sprint 65.15.0 (AC6) — TWO live regions, because "your response could not
        be sent" and "saved" are not the same announcement.
        
        Every toast in the platform used to render inside one container with
        `aria-live="polite"` / `role="status"`. A polite region waits for the
        screen reader to finish what it is saying and yields to anything the user
        does next, so a failure could be — and for a user who moves focus
        immediately, routinely is — never spoken at all. For the surfaces this
        phase covers that means a DPO is told a statutory response was delivered
        by a success toast, and told it was NOT delivered by a message their
        screen reader may drop.
        
        Errors and warnings are assertive now; success and info stay polite,
        because interrupting a screen reader mid-sentence to say "saved" is its
        own defect.
        
        BOTH regions are always mounted, empty or not. A live region created in
        the same paint as its first content is frequently not announced at all —
        the accessibility tree has nothing to diff against — which is the usual
        reason a "fixed" alert still says nothing.

        ── Correction, fix-sprint-20260915-2128 Finding 8 ─────────────────────

        The split shipped with `className="dm-toast-container"` on BOTH regions.
        That class is `position: fixed; bottom: var(--dm-toast-clearance, 0px);
        right: 0; z-index: var(--z-toast)` — so the two regions were two copies
        of one fixed box, and `data-toast-region`, added afterwards to make them
        addressable, was styled by nothing. Same corner, same z-index, assertive
        later in the DOM and therefore on top; `.dm-toast` backgrounds are fully
        opaque. A success toast live at the same time as an error was completely
        hidden, close button included — one click away on
        `ExceptionReviewPage.run()`, which raises `success` and then reloads,
        and the loader's catch raises `error`.

        `.dm-toast-container` is now the single positioned PARENT and the two
        regions flow inside it. Stacks that flow cannot overlap at any toast
        count, in either order, at any message length — a layout property rather
        than a pair of offsets someone measured. The bottom-edge anchor that
        DESIGN.md and idea-catalog #296 require is untouched: it stays on
        `.dm-toast-container`, which is still the only fixed element here.
      */}
      <div className="dm-toast-container">
        <div data-toast-region="polite" aria-live="polite" role="status">
          {toasts.filter((toast) => !ASSERTIVE_VARIANTS.has(toast.variant)).map(renderToast)}
        </div>
        <div data-toast-region="assertive" aria-live="assertive" role="alert">
          {toasts.filter((toast) => ASSERTIVE_VARIANTS.has(toast.variant)).map(renderToast)}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
